import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AUDIENCE_STATUS_LABELS } from '@marketing-hub/shared';
import { apiFetch } from '@/api/client';
import type { AudienceSummary, LeadAudienceMembership } from '@/api/types';
import { Button } from '@/components/ui/Button';
import { LoadingState } from '@/components/StateMessage/StateMessage';
import styles from './Leads.module.css';

export function LeadAudiencesSection({
  leadId,
  projectId,
}: {
  leadId: string;
  projectId: string;
}) {
  const qc = useQueryClient();
  const [pickAudienceId, setPickAudienceId] = useState('');

  const flagsQ = useQuery({
    queryKey: ['platform-context', projectId],
    queryFn: () =>
      apiFetch<{ feature_flags: Record<string, boolean> }>(
        `/api/platform/context?project_id=${projectId}`,
      ),
  });

  const enabled = Boolean(flagsQ.data?.feature_flags?.audiences_v1);

  const memberQ = useQuery({
    queryKey: ['lead-audiences', leadId],
    enabled,
    queryFn: () =>
      apiFetch<{ audiences: LeadAudienceMembership[] }>(`/api/leads/${leadId}/audiences`),
  });

  const allAudiencesQ = useQuery({
    queryKey: ['audiences', projectId],
    enabled,
    queryFn: () =>
      apiFetch<{ audiences: AudienceSummary[] }>(`/api/audiences?project_id=${projectId}`),
  });

  const memberIds = useMemo(
    () => new Set((memberQ.data?.audiences ?? []).map((a) => a.id)),
    [memberQ.data?.audiences],
  );

  const addOptions = useMemo(
    () =>
      (allAudiencesQ.data?.audiences ?? [])
        .filter((a) => a.status !== 'archived' && !memberIds.has(a.id))
        .sort((a, b) => a.name.localeCompare(b.name, 'nl')),
    [allAudiencesQ.data?.audiences, memberIds],
  );

  const addToAudience = useMutation({
    mutationFn: (audienceId: string) =>
      apiFetch(`/api/audiences/${audienceId}/members`, {
        method: 'POST',
        body: JSON.stringify({ lead_ids: [leadId], source: 'manual' }),
      }),
    onSuccess: (_data, audienceId) => {
      setPickAudienceId('');
      void qc.invalidateQueries({ queryKey: ['lead-audiences', leadId] });
      void qc.invalidateQueries({ queryKey: ['audience-members', audienceId] });
      void qc.invalidateQueries({ queryKey: ['audience', audienceId] });
      void qc.invalidateQueries({ queryKey: ['audiences'] });
    },
  });

  const removeFromAudience = useMutation({
    mutationFn: (audienceId: string) =>
      apiFetch(`/api/audiences/${audienceId}/members/${leadId}`, { method: 'DELETE' }),
    onSuccess: (_data, audienceId) => {
      void qc.invalidateQueries({ queryKey: ['lead-audiences', leadId] });
      void qc.invalidateQueries({ queryKey: ['audience-members', audienceId] });
      void qc.invalidateQueries({ queryKey: ['audience', audienceId] });
      void qc.invalidateQueries({ queryKey: ['audiences'] });
    },
  });

  if (flagsQ.isLoading || !enabled) return null;

  return (
    <section aria-label="Doelgroepen">
      <h3 className={styles.formSectionTitle}>Doelgroepen</h3>
      {memberQ.isLoading ? <LoadingState label="Doelgroepen laden…" /> : null}
      {(memberQ.data?.audiences ?? []).length ? (
        <ul className={styles.audienceMemberList}>
          {memberQ.data!.audiences.map((a) => (
            <li key={a.id} className={styles.audienceMemberItem}>
              <Link to={`/app/doelgroepen/${a.id}`} className={styles.audienceMemberLink}>
                {a.name}
              </Link>
              <span className={styles.muted}>
                {AUDIENCE_STATUS_LABELS[a.status as keyof typeof AUDIENCE_STATUS_LABELS] ?? a.status}
              </span>
              <Button
                type="button"
                variant="ghost"
                aria-label={`Verwijder uit ${a.name}`}
                disabled={removeFromAudience.isPending}
                onClick={() => removeFromAudience.mutate(a.id)}
              >
                ✕
              </Button>
            </li>
          ))}
        </ul>
      ) : (
        <p className={styles.muted}>Nog in geen doelgroep.</p>
      )}

      <div className={styles.audienceAddRow}>
        <select
          className={styles.filterInput}
          value={pickAudienceId}
          disabled={addToAudience.isPending || allAudiencesQ.isLoading}
          onChange={(e) => {
            const id = e.target.value;
            setPickAudienceId('');
            if (id) addToAudience.mutate(id);
          }}
          aria-label="Doelgroep toevoegen"
        >
          <option value="">
            {addToAudience.isPending ? 'Toevoegen…' : '+ Doelgroep kiezen…'}
          </option>
          {addOptions.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
              {a.status === 'draft' ? ' (concept)' : ''}
            </option>
          ))}
        </select>
      </div>
      {addOptions.length === 0 && !allAudiencesQ.isLoading ? (
        <p className={styles.muted}>
          Geen andere doelgroepen —{' '}
          <Link to="/app/doelgroepen">maak er een aan</Link>.
        </p>
      ) : null}
      {addToAudience.isError ? (
        <p className={styles.formError}>{(addToAudience.error as Error).message}</p>
      ) : null}
    </section>
  );
}
